"use client";

import Link from "next/link";
import { X, ChevronRight, Phone, Mail } from "lucide-react";
import { siteContent } from "@/lib/data";
import IconButton from "@/components/ui/icon-button";
import Button from "@/components/ui/button";
import { cn } from "@/lib/cn";

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function MobileNav({ open, onClose }: Props) {
  return (
    <>
      <div
        aria-hidden={!open}
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-40 bg-[color:var(--color-secondary)]/40 backdrop-blur-[2px] transition-opacity lg:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <aside
        aria-hidden={!open}
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-[85%] max-w-[22rem] bg-[color:var(--color-bg)] shadow-lift transition-transform lg:hidden",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-[color:var(--color-border)] px-4">
          <Link
            href="/"
            onClick={onClose}
            className="font-serif text-xl font-semibold tracking-tight"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            {siteContent.brand.name}
          </Link>
          <IconButton label="Close menu" onClick={onClose}>
            <X size={20} />
          </IconButton>
        </div>
        <div className="flex flex-col h-[calc(100%-4rem)] overflow-y-auto">
          <nav className="px-2 py-4">
            <ul className="flex flex-col">
              {siteContent.primaryNav.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    onClick={onClose}
                    className="flex items-center justify-between rounded-[var(--radius-sm)] px-4 py-3.5 text-base hover:bg-[color:var(--color-surface-alt)]"
                  >
                    <span>{item.label}</span>
                    <ChevronRight size={18} className="text-[color:var(--color-muted)]" />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <div className="mt-auto border-t border-[color:var(--color-border)] px-4 py-5 space-y-4">
            <Button href="/contact" variant="primary" fullWidth onClick={onClose}>
              Order free samples
            </Button>
            <div className="flex flex-col gap-2 text-sm text-[color:var(--color-muted)]">
              <a
                href={`tel:${siteContent.brand.phone.replace(/\s+/g, "")}`}
                className="inline-flex items-center gap-2 hover:text-[color:var(--color-text)]"
              >
                <Phone size={15} />
                {siteContent.brand.phone}
              </a>
              <a
                href={`mailto:${siteContent.brand.email}`}
                className="inline-flex items-center gap-2 hover:text-[color:var(--color-text)]"
              >
                <Mail size={15} />
                {siteContent.brand.email}
              </a>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
